import { Link } from "react-router-dom";
import { Camera, Calendar, Handshake, ShoppingBag, TrendingUp, Headphones, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMG } from "@/lib/site";

const COMMIT = [
  { icon: Camera, t: "Premium Production", d: "High-quality content designed to elevate your brand." },
  { icon: Calendar, t: "Personalized Strategy", d: "Custom content plans built around your goals and audience." },
  { icon: Handshake, t: "Real Partnerships", d: "We connect you with brands and opportunities that make sense." },
  { icon: ShoppingBag, t: "Merch Opportunities", d: "Turn your brand into merch and create new revenue streams." },
  { icon: TrendingUp, t: "Growth Focused", d: "Every piece of content has a purpose. Every move has a plan." },
  { icon: Headphones, t: "Dedicated Support", d: "We're with you every step of the way as you build and grow." },
];

const CHAPTERS = [
  { n: "01", t: "Our Story", body: "Fan Focus was created out of real experience in the content world. We saw too many talented creators being overlooked, underpaid, and unsupported. We built Fan Focus to change that." },
  { n: "02", t: "Our Approach", body: "We're a boutique agency, intentionally keeping our roster focused so every creator gets the attention, strategy, and resources they deserve. We don't chase numbers. We build careers." },
  { n: "03", t: "Our Mission", body: "We believe every creator has the potential to build something extraordinary. Our mission is to give you the strategy, production, and support to turn your vision into a lasting brand and a life of freedom." },
];

export default function About() {
  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6" data-testid="about-hero">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="overline text-gold mb-5">About Fan Focus</p>
            <h1 className="font-display text-5xl md:text-7xl uppercase text-white leading-[0.9]">Built On Relationships.<br /><span className="text-gold">Focused On Results.</span></h1>
            <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
              Fan Focus is a premium content management and production agency built for creators, by creators. Our mission is simple: help you turn your content into a brand and your brand into income.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="relative overflow-hidden grain">
            <img src={IMG.aboutHero} alt="" className="w-full h-[440px] object-cover object-top" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0a0a0a] my-24 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-6xl uppercase text-white">Our Commitment <span className="text-gold">To You</span></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {COMMIT.map((c, i) => (
              <Reveal key={i} delay={(i % 3) * 0.07} className="bg-[#0a0a0a]">
                <div className="p-9 h-full" data-testid={`commit-${i}`}>
                  <c.icon size={32} strokeWidth={1.2} className="text-gold mb-5" />
                  <h3 className="font-cond uppercase tracking-wide text-white mb-2">{c.t}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-8">
        {CHAPTERS.map((c, i) => (
          <Reveal key={i} delay={0.05}>
            <div className="grid md:grid-cols-12 gap-8 py-12 border-b border-white/10" data-testid={`chapter-${i}`}>
              <div className="md:col-span-3">
                <span className="font-display text-7xl md:text-8xl text-gold/80 leading-none">{c.n}</span>
              </div>
              <div className="md:col-span-9">
                <h3 className="font-display text-4xl uppercase text-white mb-4">{c.t}</h3>
                <p className="text-white/60 text-lg leading-relaxed max-w-3xl">{c.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <Marquee items={["Empower Creators", "Build Brands", "Create Legacies"]} className="mt-16" />

      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <Reveal>
          <Users size={40} strokeWidth={1.2} className="text-gold mx-auto mb-6" />
          <p className="overline text-gold mb-3">Now Accepting</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase text-white">Founding Creators</h2>
          <Link to="/pricing" data-testid="about-apply" className="mt-8 inline-flex bg-gold text-black px-10 py-4 overline hover:bg-[#F5D061] transition-colors duration-300">Apply Now</Link>
          <p className="mt-4 text-white/40 text-xs tracking-widest uppercase">Limited spots available.</p>
        </Reveal>
      </section>
    </div>
  );
}
